import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { TestBadge } from "@/components/TestBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RoleIntelligence } from "@/components/admin/RoleIntelligence";
import { UsersManager } from "@/components/admin/UsersManager";
import { EmailTemplatesManager } from "@/components/admin/EmailTemplatesManager";
import { CampaignsManager } from "@/components/admin/CampaignsManager";
import { SettingsManager } from "@/components/admin/SettingsManager";
import { SuppressionManager } from "@/components/admin/SuppressionManager";
import { CandidatesManager } from "@/components/admin/CandidatesManager";
import { StaffingManager } from "@/components/admin/StaffingManager";
import { OutboundCrm } from "@/components/admin/OutboundCrm";
import { DashboardHome } from "@/components/admin/DashboardHome";
import { GlobalSearch } from "@/components/admin/GlobalSearch";
import { ThemeToggle } from "@/components/ThemeToggle";
import {
  adminStats,
  adminSubmissions,
  adminSubmissionDetail,
  leadRecommendedCandidates,
  adminUpdateSubmission,
  adminDownloadPdf,
  adminResendNotification,
  adminRegeneratePdf,
  adminDeleteSubmission,
  adminBulkDeleteSubmissions,
  adminExportLeads,
  adminMe,
  adminLogout,
  fmtMoney,
  fmtInvestment,
} from "@/lib/api";
import { getToken, clearAuth, getUser, saveUser } from "@/lib/auth";
import {
  Loader2,
  Search,
  Users,
  DollarSign,
  Percent,
  Trophy,
  LogOut,
  Mail,
  Megaphone,
  SlidersHorizontal,
  ShieldOff,
  Briefcase,
  Phone,
  Building2,
  Download,
  RefreshCw,
  Send,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  Clock,
  Inbox,
  Database,
  Shield,
  Trash2,
  Eye,
  FileDown,
  FileSpreadsheet,
  FileText,
  ChevronDown,
  X,
  Target,
  LayoutDashboard,
  Plus,
  UserPlus,
  Settings,
  ChevronRight,
} from "lucide-react";

const STATUSES = ["New", "Contacted", "Qualified", "Proposal", "Won", "Lost"];

const STATUS_STYLES = {
  New: "bg-white/10 text-white border-white/15",
  Contacted: "bg-[#66B2FF]/15 text-[#66B2FF] border-[#66B2FF]/30",
  Qualified: "bg-[#2EE59D]/15 text-[#2EE59D] border-[#2EE59D]/30",
  Proposal: "bg-[#F7C948]/15 text-[#F7C948] border-[#F7C948]/30",
  Won: "bg-[#2EE59D]/25 text-[#2EE59D] border-[#2EE59D]/40",
  Lost: "bg-[#FF5C7A]/15 text-[#FF5C7A] border-[#FF5C7A]/30",
};

const StatusBadge = ({ status }) => (
  <Badge className={`border ${STATUS_STYLES[status] || STATUS_STYLES.New}`}>
    {status}
  </Badge>
);

const OpsPill = ({ value, kind }) => {
  // kind: 'notif' | 'pdf'
  const map = {
    sent: { icon: CheckCircle2, cls: "text-[#2EE59D] border-[#2EE59D]/30 bg-[#2EE59D]/10", label: "Sent" },
    ready: { icon: CheckCircle2, cls: "text-[#2EE59D] border-[#2EE59D]/30 bg-[#2EE59D]/10", label: "Ready" },
    pending: { icon: Clock, cls: "text-[#F7C948] border-[#F7C948]/30 bg-[#F7C948]/10", label: "Pending" },
    failed: { icon: AlertCircle, cls: "text-[#FF5C7A] border-[#FF5C7A]/30 bg-[#FF5C7A]/10", label: "Failed" },
  };
  const key = value === "sent" || value === "ready" ? value : (value === "failed" ? "failed" : "pending");
  const m = map[key];
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] rounded-full px-2 py-0.5 border ${m.cls}`}>
      <Icon size={11} /> {kind === "notif" ? "Email " : "PDF "}{m.label}
    </span>
  );
};

const KpiCard = ({ icon: Icon, label, value, accent }) => (
  <div
    className="rounded-xl border border-white/10 p-5"
    style={{ backgroundColor: "#0F1424" }}
    data-testid={`admin-kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
  >
    <div className="flex items-center gap-2 text-white/55 text-sm">
      <Icon size={16} className={accent ? "text-[#66B2FF]" : ""} /> {label}
    </div>
    <div className={`text-2xl font-bold mt-2 tabular-nums ${accent ? "text-[#66B2FF]" : "text-white"}`}>
      {value}
    </div>
  </div>
);

export default function AdminDashboard({ initialTab = "home", initialCrmView } = {}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const token = getToken();
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [industryFilter, setIndustryFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const [recCandidates, setRecCandidates] = useState(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const [notes, setNotes] = useState("");
  const [actionBusy, setActionBusy] = useState("");
  // Lead management: selection, delete confirmations, export
  const [selected, setSelected] = useState(new Set());
  const [deleteTarget, setDeleteTarget] = useState(null); // single lead row pending deletion
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(""); // "" | "filtered" | "selected" | rowId
  const [me, setMe] = useState(getUser());
  const can = (perm) => !!me?.permissions?.includes(perm);
  const canSelect = can("leads.export") || can("leads.bulk_delete");

  // Resolve a permitted default tab (used by the /crm entry route).
  const resolveInitialTab = () => {
    if (initialTab === "outbound-crm") {
      if (can("crm.view")) return "outbound-crm";
      if (can("candidates.view")) return "candidates";
      return "submissions";
    }
    if (initialTab === "candidates") {
      return can("candidates.view") ? "candidates" : "submissions";
    }
    return initialTab || "home";
  };
  const initialTabSafe = resolveInitialTab();

  // ---- command-center navigation (controlled tabs + deep links) ----
  const [activeTab, setActiveTab] = useState(initialTabSafe);
  const [crmNav, setCrmNav] = useState({
    view: initialCrmView || "all", prospectId: null, createNonce: 0, key: 0,
  });
  const [candNav, setCandNav] = useState({ id: null, createNonce: 0, key: 0 });
  const [adminNavOpen, setAdminNavOpen] = useState(false);

  const goToProspects = useCallback((view = "all") => {
    setCrmNav((n) => ({ view, prospectId: null, createNonce: 0, key: n.key + 1 }));
    setActiveTab("outbound-crm");
  }, []);
  const openProspect = useCallback((id) => {
    if (!id) return;
    setCrmNav((n) => ({ view: "all", prospectId: id, createNonce: 0, key: n.key + 1 }));
    setActiveTab("outbound-crm");
  }, []);
  const newProspect = useCallback(() => {
    setCrmNav((n) => ({ view: "all", prospectId: null, createNonce: Date.now(), key: n.key + 1 }));
    setActiveTab("outbound-crm");
  }, []);
  const goToCandidates = useCallback(() => {
    setCandNav((n) => ({ id: null, createNonce: 0, key: n.key + 1 }));
    setActiveTab("candidates");
  }, []);
  const openCandidate = useCallback((id) => {
    if (!id) return;
    setCandNav((n) => ({ id, createNonce: 0, key: n.key + 1 }));
    setActiveTab("candidates");
  }, []);
  const newCandidate = useCallback(() => {
    setCandNav((n) => ({ id: null, createNonce: Date.now(), key: n.key + 1 }));
    setActiveTab("candidates");
  }, []);

  useEffect(() => {
    if (!token) navigate("/admin");
  }, [token, navigate]);

  // Reset Prospect filters/view when leaving the Prospects tab for another
  // primary view. Bumping the CRM key remounts OutboundCrm fresh (default
  // filters) the next time the user returns to Prospects.
  const prevTabRef = useRef(activeTab);
  useEffect(() => {
    if (prevTabRef.current === "outbound-crm" && activeTab !== "outbound-crm") {
      setCrmNav((n) => ({ view: "all", prospectId: null, createNonce: 0, key: n.key + 1 }));
    }
    prevTabRef.current = activeTab;
  }, [activeTab]);

  // Refresh current user + permissions (reflects role changes immediately).
  useEffect(() => {
    if (!token) return;
    adminMe(token)
      .then((d) => {
        saveUser(d.user);
        setMe(d.user);
      })
      .catch((err) => {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          clearAuth();
          navigate("/admin");
        }
      });
  }, [token, navigate]);

  const load = useCallback(async () => {
    if (!token) return;
    // Submissions dashboard data is lead-scoped; CRM-only roles (Sales) skip it.
    if (!can("leads.view")) { setLoading(false); return; }
    setLoading(true);
    try {
      const [s, subs] = await Promise.all([
        adminStats(token),
        adminSubmissions(token, {
          status: statusFilter,
          industry: industryFilter,
          search: search || undefined,
        }),
      ]);
      setStats(s);
      setRows(subs.submissions);
      setSelected(new Set());
    } catch (err) {
      if (err?.response?.status === 401) {
        clearAuth();
        navigate("/admin");
      } else {
        toast.error("Failed to load dashboard");
      }
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter, industryFilter, search, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (id) => {
    setDetailBusy(true);
    setRecCandidates(null);
    try {
      const d = await adminSubmissionDetail(token, id);
      setDetail(d);
      setNotes(d.notes || "");
      if (can("matches.view")) {
        leadRecommendedCandidates(token, id)
          .then((r) => setRecCandidates(r.candidates))
          .catch(() => setRecCandidates([]));
      }
    } catch {
      toast.error("Failed to load submission");
    } finally {
      setDetailBusy(false);
    }
  };

  // Deep-link: /admin/dashboard?lead=<id> auto-opens the submission
  useEffect(() => {
    const leadId = searchParams.get("lead");
    if (leadId && token) openDetail(leadId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, token]);

  const closeDetail = () => {
    setDetail(null);
    if (searchParams.get("lead")) {
      searchParams.delete("lead");
      setSearchParams(searchParams, { replace: true });
    }
  };

  const handleAdminDownload = async (id) => {
    setActionBusy("pdf");
    try {
      const res = await adminDownloadPdf(token, id);
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Rendeavored-Workforce-Blueprint-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF download failed");
    } finally {
      setActionBusy("");
    }
  };

  const handleResend = async (id) => {
    setActionBusy("resend");
    try {
      await adminResendNotification(token, id);
      toast.success("Notification email sent");
      const d = await adminSubmissionDetail(token, id);
      setDetail(d);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Resend failed");
    } finally {
      setActionBusy("");
    }
  };

  const handleRegenerate = async (id) => {
    setActionBusy("regen");
    try {
      await adminRegeneratePdf(token, id);
      toast.success("PDF regenerated");
      const d = await adminSubmissionDetail(token, id);
      setDetail(d);
      load();
    } catch {
      toast.error("PDF regeneration failed");
    } finally {
      setActionBusy("");
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      const updated = await adminUpdateSubmission(token, id, { lead_status: newStatus });
      setDetail(updated);
      toast.success(`Status updated to ${newStatus}`);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const saveNotes = async () => {
    if (!detail) return;
    try {
      const updated = await adminUpdateSubmission(token, detail.id, { notes });
      setDetail(updated);
      toast.success("Notes saved");
    } catch {
      toast.error("Failed to save notes");
    }
  };

  const logout = async () => {
    try {
      await adminLogout(token);
    } catch {
      /* best-effort; clear locally regardless */
    }
    clearAuth();
    navigate("/admin");
  };

  // ---- Selection helpers ----
  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const someSelected = selected.size > 0 && !allSelected;

  const toggleSelectAll = () => {
    setSelected((prev) => {
      if (rows.length > 0 && rows.every((r) => prev.has(r.id))) {
        return new Set(); // deselect all currently shown
      }
      return new Set(rows.map((r) => r.id));
    });
  };

  const clearSelection = () => setSelected(new Set());

  // ---- Delete (single) ----
  const confirmDeleteSingle = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await adminDeleteSubmission(token, deleteTarget.id);
      setRows((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(deleteTarget.id);
        return next;
      });
      toast.success("Lead deleted");
      setDeleteTarget(null);
      adminStats(token).then(setStats).catch(() => {});
    } catch {
      toast.error("Could not delete lead. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  // ---- Delete (bulk) ----
  const confirmBulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setDeleting(true);
    try {
      const res = await adminBulkDeleteSubmissions(token, ids);
      const removed = new Set(ids);
      setRows((prev) => prev.filter((r) => !removed.has(r.id)));
      clearSelection();
      toast.success(`${res.deleted} lead${res.deleted === 1 ? "" : "s"} deleted`);
      setBulkDeleteOpen(false);
      adminStats(token).then(setStats).catch(() => {});
    } catch {
      toast.error("Bulk delete failed. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  // ---- CSV / Excel Export ----
  const doExport = async (scope, format = "csv", rowId = null) => {
    const busyKey = `${rowId || scope}:${format}`;
    setExporting(busyKey);
    try {
      const body =
        scope === "selected"
          ? { ids: rowId ? [rowId] : Array.from(selected), format }
          : {
              search: search || undefined,
              status: statusFilter,
              industry: industryFilter,
              format,
            };
      const res = await adminExportLeads(token, body);
      const mime =
        format === "xlsx"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "text/csv;charset=utf-8;";
      const blob = new Blob([res.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname =
        m ? m[1] : `teambuilder-leads-${new Date().toISOString().slice(0, 10)}.${format}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format === "xlsx" ? "Excel" : "CSV"} export ready`);
    } catch {
      toast.error("Export failed. Please try again.");
    } finally {
      setExporting("");
    }
  };


  const industries = Array.from(
    new Set(rows.map((r) => r.industry).filter(Boolean))
  );

  return (
    <div className="min-h-screen rd-bg text-white">
      {/* Top bar */}
      <header
        className="sticky top-0 z-40 border-b border-white/10 rd-topbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo height={28} themeAware />
            <TestBadge />
            <span className="hidden sm:inline text-white/40 text-sm border-l border-white/10 pl-4">
              TeamBuilder AI | CRM
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            {(can("crm.view") || can("candidates.view")) && (
              <div className="hidden md:block w-72">
                <GlobalSearch token={token} onOpenProspect={openProspect} onOpenCandidate={openCandidate} />
              </div>
            )}
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="text-white/80 text-sm font-medium" data-testid="admin-current-user">
                {me?.name || me?.email}
              </span>
              <span className="text-[#66B2FF] text-xs" data-testid="admin-current-role">
                {me?.role_label || ""}
              </span>
            </div>
            <ThemeToggle />
            <Button
              variant="ghost"
              onClick={logout}
              data-testid="admin-logout-button"
              className="text-white/70 hover:text-white hover:bg-white/5"
            >
              <LogOut size={16} className="mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <Tabs value={activeTab} onValueChange={setActiveTab} orientation="vertical" className="flex items-start">
        <aside className="hidden md:flex sticky top-16 h-[calc(100vh-4rem)] w-56 shrink-0 flex-col border-r border-white/10 bg-[#0A0F1E]/40 p-3">
          <TabsList className="flex flex-col items-stretch h-auto bg-transparent p-0 gap-1 w-full">
            <TabsTrigger
              value="home"
              data-testid="admin-tab-home"
              className="w-full justify-start data-[state=active]:bg-[#1E6BFF] data-[state=active]:text-white text-white/70 hover:bg-white/5 rounded-md px-3 py-2 flex items-center gap-2 text-sm"
            >
              <LayoutDashboard size={15} /> Home
            </TabsTrigger>
            {can("crm.view") && (
              <TabsTrigger
                value="outbound-crm"
                data-testid="admin-tab-outbound-crm"
                className="w-full justify-start data-[state=active]:bg-[#1E6BFF] data-[state=active]:text-white text-white/70 hover:bg-white/5 rounded-md px-3 py-2 flex items-center gap-2 text-sm"
              >
                <Target size={15} /> Prospects
              </TabsTrigger>
            )}
            {can("leads.view") && (
              <TabsTrigger
                value="submissions"
                data-testid="admin-tab-submissions"
                className="w-full justify-start data-[state=active]:bg-[#1E6BFF] data-[state=active]:text-white text-white/70 hover:bg-white/5 rounded-md px-3 py-2 flex items-center gap-2 text-sm"
              >
                <Inbox size={15} /> Submissions
              </TabsTrigger>
            )}
            {can("candidates.view") && (
              <TabsTrigger
                value="candidates"
                data-testid="admin-tab-candidates"
                className="w-full justify-start data-[state=active]:bg-[#1E6BFF] data-[state=active]:text-white text-white/70 hover:bg-white/5 rounded-md px-3 py-2 flex items-center gap-2 text-sm"
              >
                <Users size={15} /> Candidates
              </TabsTrigger>
            )}
            {can("placements.view") && (
              <TabsTrigger
                value="staffing"
                data-testid="admin-tab-staffing"
                className="w-full justify-start data-[state=active]:bg-[#1E6BFF] data-[state=active]:text-white text-white/70 hover:bg-white/5 rounded-md px-3 py-2 flex items-center gap-2 text-sm"
              >
                <Briefcase size={15} /> Staffing
              </TabsTrigger>
            )}
            {(can("roles.view") || can("users.view") || can("templates.manage") || can("campaigns.manage") || can("settings.manage") || can("suppressions.view")) && (
              <div className="pt-1" data-testid="admin-nav-administration">
                <button
                  type="button"
                  onClick={() => setAdminNavOpen((o) => !o)}
                  aria-expanded={adminNavOpen}
                  data-testid="admin-nav-administration-toggle"
                  className="w-full justify-start text-white/70 hover:bg-white/5 rounded-md px-3 py-2 flex items-center gap-2 text-sm"
                >
                  <Settings size={15} /> Administration
                  {adminNavOpen
                    ? <ChevronDown size={14} className="ml-auto opacity-60" />
                    : <ChevronRight size={14} className="ml-auto opacity-60" />}
                </button>
                {adminNavOpen && (
                  <div className="mt-1 ml-3 pl-3 border-l border-white/10 flex flex-col gap-1">
                    {can("roles.view") && (
                      <TabsTrigger
                        value="role-intelligence"
                        data-testid="admin-tab-role-intelligence"
                        className="w-full justify-start data-[state=active]:bg-[#1E6BFF] data-[state=active]:text-white text-white/70 hover:bg-white/5 rounded-md px-3 py-2 flex items-center gap-2 text-sm"
                      >
                        <Database size={15} /> Role Intelligence
                      </TabsTrigger>
                    )}
                    {can("users.view") && (
                      <TabsTrigger
                        value="users"
                        data-testid="admin-tab-users"
                        className="w-full justify-start data-[state=active]:bg-[#1E6BFF] data-[state=active]:text-white text-white/70 hover:bg-white/5 rounded-md px-3 py-2 flex items-center gap-2 text-sm"
                      >
                        <Shield size={15} /> Users
                      </TabsTrigger>
                    )}
                    {can("templates.manage") && (
                      <TabsTrigger
                        value="email-templates"
                        data-testid="admin-tab-email-templates"
                        className="w-full justify-start data-[state=active]:bg-[#1E6BFF] data-[state=active]:text-white text-white/70 hover:bg-white/5 rounded-md px-3 py-2 flex items-center gap-2 text-sm"
                      >
                        <Mail size={15} /> Email Templates
                      </TabsTrigger>
                    )}
                    {can("campaigns.manage") && (
                      <TabsTrigger
                        value="campaigns"
                        data-testid="admin-tab-campaigns"
                        className="w-full justify-start data-[state=active]:bg-[#1E6BFF] data-[state=active]:text-white text-white/70 hover:bg-white/5 rounded-md px-3 py-2 flex items-center gap-2 text-sm"
                      >
                        <Megaphone size={15} /> Campaigns
                      </TabsTrigger>
                    )}
                    {can("settings.manage") && (
                      <TabsTrigger
                        value="settings"
                        data-testid="admin-tab-settings"
                        className="w-full justify-start data-[state=active]:bg-[#1E6BFF] data-[state=active]:text-white text-white/70 hover:bg-white/5 rounded-md px-3 py-2 flex items-center gap-2 text-sm"
                      >
                        <SlidersHorizontal size={15} /> Settings
                      </TabsTrigger>
                    )}
                    {can("suppressions.view") && (
                      <TabsTrigger
                        value="email-suppression"
                        data-testid="admin-tab-email-suppression"
                        className="w-full justify-start data-[state=active]:bg-[#1E6BFF] data-[state=active]:text-white text-white/70 hover:bg-white/5 rounded-md px-3 py-2 flex items-center gap-2 text-sm"
                      >
                        <ShieldOff size={15} /> Email Suppression
                      </TabsTrigger>
                    )}
                  </div>
                )}
              </div>
            )}
          </TabsList>
        </aside>

        <main className="flex-1 min-w-0 px-4 sm:px-6 lg:px-8 py-6">
          <TabsContent value="home" className="mt-0">
            <DashboardHome
              token={token}
              can={can}
              userName={me?.name || me?.email}
              onOpenProspect={openProspect}
              onGoToProspects={goToProspects}
              onOpenCandidate={openCandidate}
              onGoToCandidates={goToCandidates}
              onNewProspect={newProspect}
              onNewCandidate={newCandidate}
            />
          </TabsContent>

          <TabsContent value="submissions">
            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard icon={Users} label="Total Leads" value={stats ? stats.total_leads : "-"} />
          <KpiCard
            icon={DollarSign}
            label="Pipeline Value"
            value={stats ? fmtMoney(stats.pipeline_value) : "-"}
            accent
          />
          <KpiCard icon={Percent} label="Conversion" value={stats ? `${stats.conversion_rate}%` : "-"} />
          <KpiCard icon={Trophy} label="Won Value" value={stats ? fmtMoney(stats.won_value) : "-"} />
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mt-8">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input
              data-testid="admin-submissions-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, company, email, phone..."
              className="bg-[#0F1424] border-white/10 h-11 pl-9 text-white"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger
              data-testid="admin-status-filter"
              className="bg-[#0F1424] border-white/10 h-11 text-white w-full sm:w-44"
            >
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={industryFilter} onValueChange={setIndustryFilter}>
            <SelectTrigger
              data-testid="admin-industry-filter"
              className="bg-[#0F1424] border-white/10 h-11 text-white w-full sm:w-48"
            >
              <SelectValue placeholder="Industry" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All industries</SelectItem>
              {industries.map((i) => (
                <SelectItem key={i} value={i}>
                  {i}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {can("leads.export") && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                data-testid="admin-export-csv-button"
                disabled={
                  exporting === "filtered:csv" ||
                  exporting === "filtered:xlsx" ||
                  loading ||
                  rows.length === 0 ||
                  !can("leads.export")
                }
                className="bg-[#121A2E] border border-white/10 hover:border-white/25 text-white rounded-xl h-11 px-4 shrink-0 disabled:opacity-40"
              >
                {exporting === "filtered:csv" || exporting === "filtered:xlsx" ? (
                  <Loader2 className="animate-spin mr-2" size={16} />
                ) : (
                  <FileDown size={16} className="mr-2" />
                )}
                Export
                <ChevronDown size={15} className="ml-1.5 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-[#0F1424] border-white/10 text-white"
            >
              <DropdownMenuItem
                data-testid="admin-export-filtered-csv"
                onClick={() => doExport("filtered", "csv")}
                className="text-white/85 focus:bg-white/10 focus:text-white cursor-pointer"
              >
                <FileText size={15} className="mr-2 text-[#66B2FF]" /> Export as CSV
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="admin-export-filtered-xlsx"
                onClick={() => doExport("filtered", "xlsx")}
                className="text-white/85 focus:bg-white/10 focus:text-white cursor-pointer"
              >
                <FileSpreadsheet size={15} className="mr-2 text-[#2EE59D]" /> Export as Excel (.xlsx)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          )}
        </div>

        {/* Bulk-action toolbar (appears when one or more leads are selected) */}
        {selected.size > 0 && canSelect && (
          <div
            data-testid="admin-bulk-toolbar"
            className="flex flex-col sm:flex-row sm:items-center gap-3 mt-4 rounded-xl border border-[#1E6BFF]/30 bg-[#1E6BFF]/10 px-4 py-3"
          >
            <span className="text-sm text-white font-medium" data-testid="admin-selected-count">
              {selected.size} selected
            </span>
            <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
              {can("leads.export") && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    data-testid="admin-export-selected-button"
                    disabled={exporting === "selected:csv" || exporting === "selected:xlsx"}
                    className="bg-[#121A2E] border border-white/10 hover:border-white/25 text-white rounded-lg"
                  >
                    {exporting === "selected:csv" || exporting === "selected:xlsx" ? (
                      <Loader2 className="animate-spin mr-1.5" size={14} />
                    ) : (
                      <FileDown size={14} className="mr-1.5" />
                    )}
                    Export Selected
                    <ChevronDown size={14} className="ml-1.5 opacity-60" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="bg-[#0F1424] border-white/10 text-white">
                  <DropdownMenuItem
                    data-testid="admin-export-selected-csv"
                    onClick={() => doExport("selected", "csv")}
                    className="text-white/85 focus:bg-white/10 focus:text-white cursor-pointer"
                  >
                    <FileText size={15} className="mr-2 text-[#66B2FF]" /> Export as CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="admin-export-selected-xlsx"
                    onClick={() => doExport("selected", "xlsx")}
                    className="text-white/85 focus:bg-white/10 focus:text-white cursor-pointer"
                  >
                    <FileSpreadsheet size={15} className="mr-2 text-[#2EE59D]" /> Export as Excel (.xlsx)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              )}
              {can("leads.bulk_delete") && (
              <Button
                size="sm"
                data-testid="admin-delete-selected-button"
                onClick={() => setBulkDeleteOpen(true)}
                className="bg-[#FF5C7A]/15 border border-[#FF5C7A]/40 text-[#FF5C7A] hover:bg-[#FF5C7A]/25 rounded-lg"
              >
                <Trash2 size={14} className="mr-1.5" /> Delete Selected
              </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                data-testid="admin-clear-selection-button"
                onClick={clearSelection}
                className="text-white/60 hover:text-white hover:bg-white/5 rounded-lg"
              >
                <X size={14} className="mr-1" /> Clear
              </Button>
            </div>
          </div>
        )}

        {/* Table */}
        <div
          className="rounded-xl border border-white/10 mt-6 overflow-hidden"
          style={{ backgroundColor: "#0F1424" }}
        >
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="animate-spin text-[#1E6BFF]" size={26} />
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-20 text-white/50" data-testid="admin-empty-state">
              No submissions found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    {canSelect && (
                    <TableHead className="w-10">
                      <Checkbox
                        data-testid="admin-select-all-checkbox"
                        checked={allSelected ? true : someSelected ? "indeterminate" : false}
                        onCheckedChange={toggleSelectAll}
                        aria-label="Select all leads"
                        className="border-white/30 data-[state=checked]:bg-[#1E6BFF] data-[state=checked]:border-[#1E6BFF]"
                      />
                    </TableHead>
                    )}
                    <TableHead className="text-white/60">Lead</TableHead>
                    <TableHead className="text-white/60">Company</TableHead>
                    <TableHead className="text-white/60 hidden md:table-cell">Industry</TableHead>
                    <TableHead className="text-white/60 hidden lg:table-cell">Seats</TableHead>
                    <TableHead className="text-white/60">Contract Value</TableHead>
                    <TableHead className="text-white/60">Status</TableHead>
                    <TableHead className="text-white/60 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow
                      key={row.id}
                      onClick={() => openDetail(row.id)}
                      data-testid="admin-submission-row"
                      data-state={selected.has(row.id) ? "selected" : undefined}
                      className="border-white/10 cursor-pointer hover:bg-white/5 data-[state=selected]:bg-[#1E6BFF]/10"
                    >
                      {canSelect && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          data-testid={`admin-row-checkbox-${row.id}`}
                          checked={selected.has(row.id)}
                          onCheckedChange={() => toggleRow(row.id)}
                          aria-label={`Select ${row.name || "lead"}`}
                          className="border-white/30 data-[state=checked]:bg-[#1E6BFF] data-[state=checked]:border-[#1E6BFF]"
                        />
                      </TableCell>
                      )}
                      <TableCell>
                        <div className="font-medium text-white">{row.name}</div>
                        <div className="text-white/45 text-xs">{row.email}</div>
                      </TableCell>
                      <TableCell className="text-white/80">{row.company}</TableCell>
                      <TableCell className="text-white/60 hidden md:table-cell">
                        {row.industry_label}
                      </TableCell>
                      <TableCell className="text-white/70 hidden lg:table-cell tabular-nums">
                        {row.total_headcount}
                      </TableCell>
                      <TableCell className="text-white font-medium tabular-nums">
                        {fmtMoney(row.estimated_contract_value_annual)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1.5 items-start">
                          <StatusBadge status={row.lead_status} />
                          <div className="hidden lg:flex flex-col gap-1">
                            <OpsPill value={row.notification_status} kind="notif" />
                            <OpsPill value={row.pdf_status} kind="pdf" />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()} className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            data-testid={`admin-row-view-${row.id}`}
                            title="View lead"
                            onClick={() => openDetail(row.id)}
                            className="h-8 w-8 text-white/60 hover:text-white hover:bg-white/10 rounded-lg"
                          >
                            <Eye size={15} />
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                size="icon"
                                variant="ghost"
                                data-testid={`admin-row-export-${row.id}`}
                                title="Export lead"
                                disabled={!can("leads.export") || exporting === `${row.id}:csv` || exporting === `${row.id}:xlsx`}
                                className="h-8 w-8 text-white/60 hover:text-white hover:bg-white/10 rounded-lg disabled:opacity-30"
                              >
                                {exporting === `${row.id}:csv` || exporting === `${row.id}:xlsx` ? (
                                  <Loader2 className="animate-spin" size={15} />
                                ) : (
                                  <FileDown size={15} />
                                )}
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-[#0F1424] border-white/10 text-white">
                              <DropdownMenuItem
                                data-testid={`admin-row-export-${row.id}-csv`}
                                onClick={() => doExport("selected", "csv", row.id)}
                                className="text-white/85 focus:bg-white/10 focus:text-white cursor-pointer"
                              >
                                <FileText size={15} className="mr-2 text-[#66B2FF]" /> Export as CSV
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                data-testid={`admin-row-export-${row.id}-xlsx`}
                                onClick={() => doExport("selected", "xlsx", row.id)}
                                className="text-white/85 focus:bg-white/10 focus:text-white cursor-pointer"
                              >
                                <FileSpreadsheet size={15} className="mr-2 text-[#2EE59D]" /> Export as Excel (.xlsx)
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          {can("leads.delete") && (
                          <>
                          <span className="mx-0.5 h-5 w-px bg-white/10" aria-hidden="true" />
                          <Button
                            size="icon"
                            variant="ghost"
                            data-testid={`admin-row-delete-${row.id}`}
                            title="Delete lead"
                            onClick={() => setDeleteTarget(row)}
                            className="h-8 w-8 text-[#FF5C7A]/80 hover:text-[#FF5C7A] hover:bg-[#FF5C7A]/10 rounded-lg"
                          >
                            <Trash2 size={15} />
                          </Button>
                          </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
        <p className="text-white/40 text-xs mt-3">{rows.length} result(s)</p>
          </TabsContent>

          {can("roles.view") && (
            <TabsContent value="role-intelligence">
              <RoleIntelligence token={token} />
            </TabsContent>
          )}

          {can("users.view") && (
            <TabsContent value="users">
              <UsersManager token={token} me={me} />
            </TabsContent>
          )}

          {can("templates.manage") && (
            <TabsContent value="email-templates">
              <EmailTemplatesManager token={token} />
            </TabsContent>
          )}

          {can("campaigns.manage") && (
            <TabsContent value="campaigns">
              <CampaignsManager token={token} />
            </TabsContent>
          )}

          {can("settings.manage") && (
            <TabsContent value="settings">
              <SettingsManager token={token} />
            </TabsContent>
          )}

          {can("suppressions.view") && (
            <TabsContent value="email-suppression">
              <SuppressionManager token={token} />
            </TabsContent>
          )}

          {can("candidates.view") && (
            <TabsContent value="candidates">
              <CandidatesManager key={`cand-${candNav.key}`} token={token} openCandidateId={candNav.id} openIntakeOnMount={!!candNav.createNonce} />
            </TabsContent>
          )}

          {can("placements.view") && (
            <TabsContent value="staffing">
              <StaffingManager token={token} />
            </TabsContent>
          )}

          {can("crm.view") && (
            <TabsContent value="outbound-crm">
              <OutboundCrm
                key={`crm-${crmNav.key}`}
                token={token}
                initialView={crmNav.view || initialCrmView}
                openProspectId={crmNav.prospectId}
                openCreateNonce={crmNav.createNonce}
              />
            </TabsContent>
          )}
        </main>
      </Tabs>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && closeDetail()}>
        <DialogContent
          className="max-w-2xl bg-[#0F1424] border-white/10 text-white max-h-[90vh] overflow-y-auto"
          data-testid="admin-detail-dialog"
        >
          {detailBusy && !detail ? (
            <div className="flex justify-center py-10">
              <Loader2 className="animate-spin text-[#1E6BFF]" size={24} />
            </div>
          ) : detail ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-white text-xl">
                  {detail.lead?.name} &middot; {detail.lead?.company}
                </DialogTitle>
              </DialogHeader>

              <div className="grid sm:grid-cols-2 gap-3 mt-2">
                <div className="flex items-center gap-2 text-sm text-white/70">
                  <Mail size={15} className="text-[#66B2FF]" /> {detail.lead?.email}
                </div>
                <div className="flex items-center gap-2 text-sm text-white/70">
                  <Phone size={15} className="text-[#66B2FF]" /> {detail.lead?.phone}
                </div>
                <div className="flex items-center gap-2 text-sm text-white/70">
                  <Building2 size={15} className="text-[#66B2FF]" /> {detail.result?.industry_label}
                </div>
                <div className="flex items-center gap-2 text-sm text-white/70">
                  <DollarSign size={15} className="text-[#66B2FF]" />
                  {fmtMoney(detail.estimated_contract_value_annual)} / yr
                </div>
              </div>

              {/* Ops status + actions */}
              <div className="mt-4 rounded-lg border border-white/10 p-3" style={{ backgroundColor: "#0A0F1E" }}>
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <OpsPill value={detail.notification_status} kind="notif" />
                  <OpsPill value={detail.pdf_status} kind="pdf" />
                  <span className="text-white/40 text-xs ml-auto">
                    Submitted {detail.submitted_at ? new Date(detail.submitted_at).toLocaleString() : "-"}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    data-testid="admin-download-pdf-button"
                    onClick={() => handleAdminDownload(detail.id)}
                    disabled={actionBusy === "pdf"}
                    className="bg-[#1E6BFF] hover:bg-[#3B82F6] text-white rounded-lg"
                  >
                    {actionBusy === "pdf" ? <Loader2 className="animate-spin" size={14} /> : <Download size={14} className="mr-1" />} Download PDF
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    data-testid="admin-view-blueprint-button"
                    onClick={() => window.open(`/blueprint/${detail.id}`, "_blank")}
                    className="bg-[#121A2E] border border-white/10 hover:border-white/25 text-white rounded-lg"
                  >
                    <ExternalLink size={14} className="mr-1" /> View blueprint
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    data-testid="admin-resend-notification-button"
                    onClick={() => handleResend(detail.id)}
                    disabled={actionBusy === "resend"}
                    className="bg-[#121A2E] border border-white/10 hover:border-white/25 text-white rounded-lg"
                  >
                    {actionBusy === "resend" ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} className="mr-1" />} Resend email
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    data-testid="admin-regenerate-pdf-button"
                    onClick={() => handleRegenerate(detail.id)}
                    disabled={actionBusy === "regen"}
                    className="bg-[#121A2E] border border-white/10 hover:border-white/25 text-white rounded-lg"
                  >
                    {actionBusy === "regen" ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} className="mr-1" />} Regenerate PDF
                  </Button>
                </div>
                <div className="text-white/35 text-[11px] mt-2">Assessment ID: {detail.id}</div>
              </div>

              {/* Status pipeline */}
              <div className="mt-5" data-testid="admin-lead-pipeline">
                <div className="text-white/60 text-sm mb-2">Lead status</div>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((s) => (
                    <button
                      key={s}
                      data-testid={`admin-status-option-${s}`}
                      onClick={() => updateStatus(detail.id, s)}
                      className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${
                        detail.lead_status === s
                          ? STATUS_STYLES[s]
                          : "border-white/10 text-white/50 hover:text-white hover:border-white/25"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Recommended roles */}
              <div className="mt-5">
                <div className="text-white/60 text-sm mb-2">Recommended roles</div>
                <div className="rounded-lg border border-white/10 divide-y divide-white/5" style={{ backgroundColor: "#0A0F1E" }}>
                  {detail.result?.roles?.map((r) => (
                    <div key={r.key} className="flex items-center justify-between px-4 py-2.5">
                      <span className="text-white/85 text-sm">
                        {r.quantity}x {r.name}
                      </span>
                      <span className="text-white/50 text-xs tabular-nums">
                        {fmtInvestment(r.line_low, r.line_high)}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-2 text-sm">
                  <span className="text-white/60">Estimated Deal Value</span>
                  <span className="text-white font-semibold tabular-nums">
                    {fmtInvestment(detail.result?.investment_low, detail.result?.investment_high)}
                  </span>
                </div>
              </div>

              {/* Assessment answers */}
              <div className="mt-5">
                <div className="text-white/60 text-sm mb-2">Assessment</div>
                <div className="flex flex-wrap gap-2 text-xs">
                  {detail.assessment?.pain_points?.map((p) => (
                    <span key={p} className="rounded-full px-2.5 py-1 bg-white/5 border border-white/10 text-white/70">
                      {p.replace(/_/g, " ")}
                    </span>
                  ))}
                </div>
                <div className="text-white/50 text-xs mt-2">
                  Size: {detail.assessment?.company_size} &middot; Workload: {detail.assessment?.workload} &middot; Growth: {detail.assessment?.growth_goal}
                </div>
              </div>

              {/* Notes */}
              <div className="mt-5">
                <div className="text-white/60 text-sm mb-2">Internal notes</div>
                <Textarea
                  data-testid="admin-notes-textarea"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add notes about this lead..."
                  className="bg-[#0A0F1E] border-white/10 text-white min-h-[90px]"
                />
                <Button
                  onClick={saveNotes}
                  data-testid="admin-save-notes-button"
                  className="mt-3 bg-[#1E6BFF] hover:bg-[#3B82F6] rounded-xl"
                >
                  Save notes
                </Button>
              </div>

              {can("matches.view") && (
                <div className="mt-6" data-testid="lead-recommended-candidates">
                  <div className="text-white/70 text-sm font-medium mb-3 flex items-center gap-1.5">
                    <Users size={15} className="text-[#66B2FF]" /> Recommended Candidates
                  </div>
                  {recCandidates === null ? (
                    <div className="text-white/40 text-sm flex items-center gap-2">
                      <Loader2 className="animate-spin" size={14} /> Finding best matches&hellip;
                    </div>
                  ) : recCandidates.length === 0 ? (
                    <div className="text-white/40 text-sm">No candidates in the Talent Network yet.</div>
                  ) : (
                    <div className="space-y-2">
                      {recCandidates.slice(0, 5).map((c, idx) => (
                        <div key={c.candidate_id} role="button" tabIndex={0}
                          onClick={() => openCandidate(c.candidate_id)}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openCandidate(c.candidate_id); } }}
                          aria-label={`Open candidate ${c.name}, ${c.overall} percent match`}
                          className={`rounded-xl border p-3 cursor-pointer transition-colors hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#66B2FF] ${idx === 0 ? "border-[#1E6BFF]/40 bg-[#1E6BFF]/5" : "border-white/10"}`}
                          data-testid="lead-candidate-match">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <div className="text-white font-medium flex items-center gap-2">
                                {c.name}
                                {idx === 0 && <span className="text-[10px] bg-[#1E6BFF] text-white rounded px-1.5 py-0.5">TOP MATCH</span>}
                              </div>
                              <div className="text-white/45 text-xs">
                                {c.current_position || "\u2014"} &middot; {c.status} &middot; {c.availability || "\u2014"}
                              </div>
                            </div>
                            <div className="text-xl font-bold text-[#66B2FF] tabular-nums">{c.overall}%</div>
                          </div>
                          {(c.strengths || []).length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {c.strengths.slice(0, 4).map((s, i) => (
                                <span key={i} className="text-xs bg-[#2EE59D]/10 text-[#2EE59D] rounded-md px-2 py-0.5">{s}</span>
                              ))}
                            </div>
                          )}
                          {(c.gaps || []).length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-1.5">
                              {c.gaps.slice(0, 3).map((s, i) => (
                                <span key={i} className="text-xs bg-[#F5B94A]/10 text-[#F5B94A] rounded-md px-2 py-0.5">{s}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Single-lead delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && !deleting && setDeleteTarget(null)}>
        <AlertDialogContent
          className="bg-[#0F1424] border-white/10 text-white"
          data-testid="admin-delete-single-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete this lead?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              This will permanently remove{" "}
              <span className="text-white font-medium">
                {deleteTarget?.name || "this lead"}
              </span>
              {deleteTarget?.company ? (
                <> from <span className="text-white font-medium">{deleteTarget.company}</span></>
              ) : null}
              {deleteTarget?.email ? (
                <> (<span className="text-white/80">{deleteTarget.email}</span>)</>
              ) : null}
              . This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={deleting}
              data-testid="admin-delete-single-cancel"
              className="bg-transparent border-white/15 text-white/80 hover:bg-white/5 hover:text-white"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDeleteSingle(); }}
              disabled={deleting}
              data-testid="admin-delete-single-confirm"
              className="bg-[#FF5C7A] hover:bg-[#ff456a] text-white"
            >
              {deleting ? <Loader2 className="animate-spin mr-2" size={15} /> : <Trash2 size={15} className="mr-2" />}
              Delete lead
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk delete confirmation */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={(o) => !o && !deleting && setBulkDeleteOpen(false)}>
        <AlertDialogContent
          className="bg-[#0F1424] border-white/10 text-white"
          data-testid="admin-delete-bulk-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              Delete {selected.size} selected lead{selected.size === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              This will permanently remove {selected.size} lead{selected.size === 1 ? "" : "s"} from
              the database. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={deleting}
              data-testid="admin-delete-bulk-cancel"
              className="bg-transparent border-white/15 text-white/80 hover:bg-white/5 hover:text-white"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmBulkDelete(); }}
              disabled={deleting}
              data-testid="admin-delete-bulk-confirm"
              className="bg-[#FF5C7A] hover:bg-[#ff456a] text-white"
            >
              {deleting ? <Loader2 className="animate-spin mr-2" size={15} /> : <Trash2 size={15} className="mr-2" />}
              Delete {selected.size} lead{selected.size === 1 ? "" : "s"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
